import type React from 'react'
import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { languageDirection } from '@knownow/domain'
import { supabase } from '@/lib/supabase'
import { Button } from '@knownow/ui'

const AGENT_URL =
  (import.meta.env['VITE_AGENT_URL'] as string | undefined) ?? 'http://127.0.0.1:3001'

interface PlanPreview {
  title: string
  description: string
  timeBudgetMinutes: number
  preferredLanguage?: string
}

export function WelcomePage(): React.ReactElement {
  const { shareToken } = useParams<{ shareToken: string }>()
  const navigate = useNavigate()
  const [plan, setPlan] = useState<PlanPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [jwt, setJwt] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function init(): Promise<void> {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        let token = session?.access_token ?? null

        if (!token) {
          const { data, error: authError } = await supabase.auth.signInAnonymously()
          if (authError || !data.session) {
            if (!cancelled) {
              setError('Failed to initialize session')
              setLoading(false)
            }
            return
          }
          token = data.session.access_token
        }

        if (!cancelled) setJwt(token)

        const res = await fetch(
          `${AGENT_URL}/respondent/plan-preview?shareToken=${encodeURIComponent(shareToken ?? '')}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )

        if (cancelled) return

        if (!res.ok) {
          setError(res.status === 404 ? 'This survey link is invalid or has expired.' : 'Something went wrong.')
          setLoading(false)
          return
        }

        const data = (await res.json()) as PlanPreview
        setPlan(data)
        setLoading(false)
      } catch {
        if (!cancelled) {
          setError('Something went wrong.')
          setLoading(false)
        }
      }
    }

    init()
    return () => { cancelled = true }
  }, [shareToken])

  const handleStart = useCallback(async (): Promise<void> => {
    if (!jwt || !shareToken || starting) return
    setStarting(true)

    try {
      const res = await fetch(`${AGENT_URL}/respondent/start-conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ shareToken }),
      })

      if (!res.ok) {
        setError('Failed to start conversation.')
        setStarting(false)
        return
      }

      const { conversationId, planId, organizationId, preferredLanguage } = (await res.json()) as {
        conversationId: string
        planId: string
        organizationId: string
        preferredLanguage?: string
      }
      const lang = preferredLanguage ?? plan?.preferredLanguage ?? 'en'
      navigate(
        `/c/${shareToken}/${conversationId}?planId=${planId}&organizationId=${organizationId}&lang=${encodeURIComponent(lang)}`,
      )
    } catch {
      setError('Failed to start conversation.')
      setStarting(false)
    }
  }, [jwt, shareToken, starting, navigate, plan])

  const dir = languageDirection(plan?.preferredLanguage)

  if (error) {
    return (
      <main
        dir={dir}
        lang={plan?.preferredLanguage ?? undefined}
        className="flex min-h-screen items-center justify-center bg-neutral-50 p-4"
      >
        <div className="max-w-md text-center" data-testid="error-message">
          <p className="text-sm text-error-700">{error}</p>
        </div>
      </main>
    )
  }

  if (loading || !plan) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-neutral-50">
        <p className="text-sm text-neutral-500">Loading...</p>
      </main>
    )
  }

  return (
    <main
      dir={dir}
      lang={plan.preferredLanguage}
      data-testid="welcome-root"
      className="flex min-h-screen items-center justify-center bg-neutral-50 p-4"
    >
      <div className="w-full max-w-lg rounded-xl border border-neutral-200 bg-neutral-0 p-8 shadow-sm">
        <h1 className="text-2xl font-bold font-serif text-neutral-900 mb-2">{plan.title}</h1>
        <p className="text-sm text-neutral-600 mb-6">{plan.description}</p>

        <div className="flex items-center gap-2 mb-6 text-sm text-neutral-500">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 4v4l3 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span>Estimated time: {plan.timeBudgetMinutes} minutes</span>
        </div>

        <p className="text-xs text-neutral-400 mb-6" data-testid="consent-note">
          Your responses will be collected anonymously. You can optionally provide your email at the end.
        </p>

        <Button
          variant="primary"
          size="lg"
          onClick={handleStart}
          disabled={starting}
          data-testid="start-button"
        >
          {starting ? 'Starting...' : 'Start conversation'}
        </Button>
      </div>
    </main>
  )
}
